import { MEDIA_ERROR_CODES, VERIFICATION_ERROR_CODES } from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** What the review surface should say about a refusal. */
export interface VerificationErrorCopy {
  /** A `verification:error.*` key. */
  i18nKey: string;
  /**
   * A colleague moved the case between the decision and the write (409).
   *
   * The one refusal whose answer is **re-read**, not retry and not "you did
   * something wrong": the move was legal when it was chosen. The screen shows a
   * banner and refetches rather than a red error under a button.
   */
  isConflict: boolean;
  /**
   * A refusal by **authority** rather than by state.
   *
   * The distinction is what the operator needs, and P10.6j is the phase that
   * found the two need different fields: "you may not review this member" never
   * improves — there is no retry, no state to wait for, and no permission to be
   * granted — so the screen renders itself read-only and says so, where a state
   * refusal is a message under the control that produced it.
   */
  isAuthorityRefusal: boolean;
  /** The step keys the api named, on the one refusal that names any. */
  steps: readonly string[];
}

/**
 * The refusals this surface can meet, and the key that answers each.
 *
 * **Two vocabularies, deliberately.** The case detail also *mints evidence*, so
 * `media_*` refusals reach the same screen — and a reviewer refused a photograph
 * because of gender-matched review is having the same conversation as one refused
 * the case. Mapping only the verification codes would leave the media half
 * falling through to "something went wrong" beside a case that explains itself.
 *
 * Codes come from the contracts constants rather than string literals (U3): they
 * are the same objects the api's `DomainError` subclasses read, so a rename is a
 * compile error instead of a silent fallback.
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [VERIFICATION_ERROR_CODES.caseNotFound]: 'error.caseNotFound',
  [VERIFICATION_ERROR_CODES.illegalCaseTransition]: 'error.illegalTransition',
  [VERIFICATION_ERROR_CODES.caseChanged]: 'error.caseChanged',
  [VERIFICATION_ERROR_CODES.stepNotInCase]: 'error.stepNotInCase',
  [VERIFICATION_ERROR_CODES.stepNotSubmittable]: 'error.stepNotSubmittable',
  [VERIFICATION_ERROR_CODES.caseNotEditable]: 'error.caseNotEditable',
  [VERIFICATION_ERROR_CODES.stepAlreadyResolved]: 'error.stepAlreadyResolved',
  [VERIFICATION_ERROR_CODES.nothingToAccept]: 'error.nothingToAccept',
  [VERIFICATION_ERROR_CODES.evidenceNotAcceptable]: 'error.evidenceNotAcceptable',
  [VERIFICATION_ERROR_CODES.evidenceNotReady]: 'error.evidenceNotReady',
  [VERIFICATION_ERROR_CODES.caseNotReady]: 'error.caseNotReady',
  [VERIFICATION_ERROR_CODES.interviewNotFound]: 'error.interviewNotFound',
  // D9a — the paid, slot-booked interview. The first three are the MEMBER's
  // refusals and reach a reviewer only through a re-drive; the last four are the
  // availability calendar's own (D9b draws it) and are named here so the copy
  // parity guard holds from the day the codes exist.
  [VERIFICATION_ERROR_CODES.interviewEvidenceNotApproved]: 'error.interviewEvidenceNotApproved',
  [VERIFICATION_ERROR_CODES.interviewFeeRequired]: 'error.interviewFeeRequired',
  [VERIFICATION_ERROR_CODES.interviewSlotUnavailable]: 'error.interviewSlotUnavailable',
  [VERIFICATION_ERROR_CODES.interviewRescheduleCutoff]: 'error.interviewRescheduleCutoff',
  [VERIFICATION_ERROR_CODES.interviewSlotInUse]: 'error.interviewSlotInUse',
  [VERIFICATION_ERROR_CODES.interviewSlotOverlap]: 'error.interviewSlotOverlap',
  [VERIFICATION_ERROR_CODES.interviewSlotWindowInvalid]: 'error.interviewSlotWindowInvalid',
  [VERIFICATION_ERROR_CODES.interviewSlotNotFound]: 'error.interviewSlotNotFound',
  [VERIFICATION_ERROR_CODES.interviewSlotLinkRequired]: 'error.interviewSlotLinkRequired',
  [VERIFICATION_ERROR_CODES.genderMismatch]: 'error.genderMismatch',
  // The three D6 refusals. The first two are the MEMBER's to meet (an edit
  // while a reviewer holds the case); a reviewer sees them only on a re-drive.
  [VERIFICATION_ERROR_CODES.reReviewUnderReview]: 'error.reReviewUnderReview',
  [VERIFICATION_ERROR_CODES.reReviewDeciding]: 'error.reReviewDeciding',
  [VERIFICATION_ERROR_CODES.decisionReasonNotApplicable]: 'error.decisionReasonNotApplicable',
  [MEDIA_ERROR_CODES.genderMismatch]: 'error.genderMismatch',
  [MEDIA_ERROR_CODES.forbidden]: 'error.evidenceForbidden',
};

/**
 * Refusals about **who may look**, not about where anything stands.
 *
 * Each of these is permanent for this reviewer and this member, which is why the
 * screen stops offering controls rather than leaving them to fail.
 */
const AUTHORITY_CODES: ReadonlySet<string> = new Set<string>([
  VERIFICATION_ERROR_CODES.genderMismatch,
  MEDIA_ERROR_CODES.genderMismatch,
  MEDIA_ERROR_CODES.forbidden,
]);

/** Turns an api refusal into what this surface says about it, or `null`. */
export function verificationErrorCopy(
  error: ApiError | null | undefined,
): VerificationErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const i18nKey = KEY_BY_CODE[error.code];

  if (i18nKey === undefined) {
    return null;
  }

  return {
    i18nKey,
    isConflict: error.code === VERIFICATION_ERROR_CODES.caseChanged,
    isAuthorityRefusal: AUTHORITY_CODES.has(error.code),
    // `CaseNotReadyToSubmitError` is the only refusal carrying a list, and
    // naming the outstanding steps is the difference between an answer and a
    // shrug. Read defensively: `details` is the api's shape, not this client's.
    steps: readSteps(error.details?.['steps']),
  };
}

function readSteps(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}
